<script>
	import Lightbox from "../Lightbox/Lightbox.svelte";
	import { damageReviewedData, imageLightbox } from "../../store/store";
	import CustomInfiniteSelect from "../CustomSelect/CustomInfiniteSelect.svelte";
	import { splitCamelCaseAndCapitalize } from "src/utils/misc";
	import { navigate } from "svelte-routing";
	import { createEventDispatcher } from "svelte";

	export let data = {};
	export let isRadioButtonsEnabled;
	export let options = [];
	export let value = null;
	export let onSelectReport = () => {};
	export let onLoadMore = () => {};
	export let caseManagement = false;
	export let side = "left";
	export let editablePanel = ""; // used to disable radio buttons for a specific report when in view mode

	let idx = 0; // current image index
	let activeReportIndex = 0; // index of the active report in the options array
	let damageAssessment = "none"; // for the radio buttons
	let isOpen = false; // lightbox visibility
	let lightboxIndex = 0;
	let selectedReportOption = null; // holds the currently displayed report option
	let dispatch = createEventDispatcher();
	let arr = $damageReviewedData; // to store the attribution data

	$: images = data?.images ?? [];
	$: hasL1Investigated = data?.hasL1Investigated ?? false;
	$: isPrevDisabled = idx === 0;
	$: isNextDisabled = images.length ? idx === images.length - 1 : true;
	$: isPanelDisabled = editablePanel !== "" && editablePanel !== data.count;

	// resets the active report index if it goes out of bounds
	$: if (options.length > 0 && activeReportIndex >= options.length) {
		activeReportIndex = 0;
	}

	// finds the matching option from the 'options' array based on the 'value' prop
	// ensures the component can be initialized with a specific report selected
	$: matchedOption =
		value && options.length > 0
			? options.find((o) => o.value === value.value)
			: null;

	// sets initial index based on the prop
	$: if (matchedOption) {
		activeReportIndex = options.indexOf(matchedOption);
	}

	// determines the report to display, falls back to the active index, and defaults to the first option
	$: selectedReportOption = value;

	$: isEmpty = !value;

	$: if (data?.images && data.images.length > 0) {
		let modified = false;
		for (const img of data.images) {
			// initialize damageAttribute if missing or null
			if (!("damageAttribute" in img) || img.damageAttribute === null) {
				if (img.isHighlighted) {
					// auto-select "new damage" if backend flag is true
					img.damageAttribute = caseManagement ? true : "new-damage";
					modified = true;
				} else if (!("damageAttribute" in img)) {
					// default to "none" for new images
					img.damageAttribute = caseManagement ? null : "none";
				}
			}
		}

		// update global store if any damageAttribute was automatically set
		if (modified && data.bookingId) {
			const existingIndex = $damageReviewedData.findIndex(
				(item) =>
					item.bookingId === data.bookingId &&
					item.reportType === data.reportType
			);
			if (existingIndex === -1) {
				damageReviewedData.update((items) => [...items, data]);
			} else {
				damageReviewedData.set($damageReviewedData);
			}
		}
	}

	function handleAttributionChange(e) {
		if (!data?.images || !data.bookingId) return;

		let val = e.target.value;
		if (val === "null") val = null;
		else if (val === "true") val = true;
		else if (val === "false") val = false;

		data.images[idx].damageAttribute = val;

		// check if object with bookingId already exists in the store
		const existingIndex = arr.findIndex(
			(item) =>
				item.bookingId === data.bookingId &&
				item.reportType === data.reportType
		);
		if (existingIndex !== -1) {
			// if exists, update the existing object
			arr[existingIndex].images.damageAttribute =
				data.images[idx].damageAttribute;

			const allNone = arr[existingIndex].images.every(
				(item) =>
					item.damageAttribute === "" || item.damageAttribute === null
			);
			if (allNone) {
				arr.splice(existingIndex, 1);
			}
			damageReviewedData.set(arr);
			return;
		}

		arr.push(data);
		damageReviewedData.set(arr);
		dispatch("localDataUpdated");
	}

	function handleAttributionOnMount() {
		// check store for existing attribution data
		// if store has data with bookingId and uid and imgSide, set the data.images[idx].damageAttribute
		if (!data?.images || !data.bookingId) return;

		const existingIndex = arr.findIndex(
			(item) =>
				item.bookingId === data.bookingId &&
				item.reportType === data.reportType
		);
		if (existingIndex !== -1) {
			data = arr[existingIndex];
		}
	}

	$: if (typeof idx === "number") {
		handleAttributionOnMount();
	}

	function handleDamageAssessmentChange(e) {
		let val = e.target.value;
		data.images[idx].damageAttribute = val;

		damageReviewedData.set([data]);
		dispatch("cardActive", {
			value: e.target.value,
			data: data.images,
		});
	}

	$: {
		if (data?.images?.length > 0 && data.images[idx]) {
			damageAssessment =
				data.images[idx].damageAttribute !== null &&
				data.images[idx].damageAttribute !== "none"
					? "new-damage"
					: "none";
		} else {
			damageAssessment = "none";
		}
	}

	// functions for image navigation
	function goToPrevImage() {
		if (idx > 0) {
			idx -= 1;
		}
	}

	function goToNextImage() {
		if (idx < images.length - 1) {
			idx += 1;
		}
	}

	// determines CSS class for the report type tag (pre or post)
	function getReportTypeClass(type) {
		const normalizedType = type?.toLowerCase();
		if (normalizedType?.includes("pre")) {
			return "tag tag-pre";
		}
		if (normalizedType?.includes("post")) {
			return "tag tag-post";
		}
		if (normalizedType?.includes("active")) {
			return "tag tag-active";
		}
	}

	// opens the lightbox with the report images
	function openLightbox() {
		imageLightbox.set(images.map((img) => img.imgUrl));
		lightboxIndex = idx;
		isOpen = true;
	}

	function handleLightboxIdxChange(newIdx) {
		idx = newIdx;
	}

	// handles the selection change from the CustomInfiniteSelect component
	function handleCaseChange(option) {
		const optionIdx = options.findIndex(
			(o) => o.value === option.value || o.bookingId === option.bookingId
		);
		activeReportIndex = optionIdx >= 0 ? optionIdx : 0;
		selectedReportOption = option;
		onSelectReport(option);
	}

	// function is passed to CustomInfiniteSelect
	// it allows the child component to directly change the active report
	function changeIdx(idx) {
		if (idx == null || idx < 0 || idx >= options.length) return;
		activeReportIndex = idx;
		selectedReportOption = options[idx];
	}

	function changeImageIdx(index) {
		if (index == null || index < 0 || index >= images.length) return;
		idx = index;
	}
</script>

{#if isOpen}
	<Lightbox
		imageShowingIndex={lightboxIndex}
		changeIdx={handleLightboxIdxChange}
		on:click={() => (isOpen = false)}
	/>
{/if}

<div class="compare-card">
	<!-- dropdown to select a report to display -->
	<CustomInfiniteSelect
		{options}
		value={selectedReportOption}
		onChange={handleCaseChange}
		{onLoadMore}
		showNavArrows={true}
		showCopyButton={true}
		{changeIdx}
		selectedLabelKey="bookingNo"
		placeholder="-"
		{changeImageIdx}
	/>

	<!-- report details section -->
	<div class="details">
		<div class="details-row space-between">
			<div class="d-flex align-items-center gap-1 info-block">
				<span>Report No: </span>
				<span>{data?.bookingId || "-"}</span>
			</div>
			{#if data?.isLastReviewed}
				<span class="pill pill-green">Last Reviewed</span>
			{/if}
			{#if hasL1Investigated}
				<span class="pill">L1 Investigated</span>
			{/if}
		</div>

		<div class="details-row">
			<span>Report Type: </span>
			<span class={getReportTypeClass(data?.reportType)}>
				{splitCamelCaseAndCapitalize(data?.reportType) || "-"}
			</span>
		</div>
		<div class="details-row">
			<span>Case Attributed: </span>
			{#if data.cases && data.cases.length > 0}
				{#each data.cases as { caseNo, caseId }}
					<button
						type="button"
						class="link link-text btn-link-reset"
						on:click={() =>
							navigate(
								`/issues/case-management/quotation/${caseId}`
							)}
					>
						{caseNo || ""}
					</button>
				{/each}
			{:else}
				<span>-</span>
			{/if}
		</div>
	</div>

	<hr />

	{#if isEmpty}
		<div
			class="d-flex flex-fill align-items-center justify-content-center fw-bold fs-5"
		>
			-
		</div>
	{:else}
		<!-- image viewer section -->
		<div class="image-section">
			<div class="image-title">
				{data?.images && data.images.length > 0
					? splitCamelCaseAndCapitalize(data.images[idx]?.imgSide)
					: ""}
			</div>

			<div class="image-viewer">
				<button
					class={`image-nav ${
						isPrevDisabled ? "arrow-disabled" : ""
					}`}
					aria-label="Previous"
					on:click={goToPrevImage}
					disabled={idx === 0}
				>
					<img
						src="/assets/icon-chevron-left-thin.svg"
						alt="Previous"
					/>
				</button>

				<div
					class={`image-frame ${
						images[idx]?.isDamage ? "damage" : ""
					}`}
					on:click={openLightbox}
					on:keydown={openLightbox}
				>
					<img src={images[idx]?.imgUrl} alt="Report view" />
					{#if images[idx]?.isDamage}
						<span class="alert-dot" aria-hidden="true" />
					{/if}
				</div>

				<button
					class={`image-nav ${
						isNextDisabled ? "arrow-disabled" : ""
					}`}
					aria-label="Next"
					on:click={goToNextImage}
					disabled={idx === images.length - 1}
				>
					<img src="/assets/icon-chevron-right-thin.svg" alt="Next" />
				</button>
			</div>

			<!-- radio buttons for damage assessment -->
			<div class="radio-group">
				{#if data.images && data.images.length > 0}
					{#if caseManagement}
						<label class="radio-option px-5">
							<input
								type="radio"
								value={null}
								on:change={(e) => handleAttributionChange(e)}
								disabled={data.cases?.length > 0}
								bind:group={data.images[idx].damageAttribute}
							/>
							<span>No attribution needed</span>
						</label>

						{#if data.reportType !== "preTrip"}
							<label class="radio-option px-5">
								<input
									type="radio"
									value={true}
									on:change={(e) =>
										handleAttributionChange(e)}
									disabled={data.cases?.length > 0}
									bind:group={
										data.images[idx].damageAttribute
									}
								/>
								<span>Attribute new damage to this BID</span>
							</label>
						{/if}

						<label class="radio-option px-5">
							<input
								type="radio"
								value={false}
								on:change={(e) => handleAttributionChange(e)}
								disabled={data.cases?.length > 0}
								bind:group={data.images[idx].damageAttribute}
							/>
							<span>Attribute new damage to unknown</span>
						</label>
					{:else}
						<label class="radio-option">
							<input
								type="radio"
								value="none"
								on:change={(e) =>
									handleDamageAssessmentChange(e)}
								checked={data.images[idx].damageAttribute ===
									"none" ||
									data.images[idx].damageAttribute === null}
								disabled={isPanelDisabled ||
									!isRadioButtonsEnabled}
								name={`${data.bookingId}-${data.type}-${idx}-${side}`}
							/>
							<span>No new damage in this photo</span>
						</label>
						<label class="radio-option">
							<input
								type="radio"
								value="new-damage"
								on:change={(e) =>
									handleDamageAssessmentChange(e)}
								checked={data.images[idx].damageAttribute ===
									"new-damage"}
								disabled={isPanelDisabled ||
									!isRadioButtonsEnabled}
								name={`${data.bookingId}-${data.type}-${idx}-${side}`}
							/>
							<span>New damage in this photo</span>
						</label>
					{/if}
				{/if}
			</div>

			<!-- thumbnails for image navigation -->
			<div class="thumbnails">
				{#each images as img, index}
					<button
						type="button"
						class={`thumbnail ${index === idx ? "active" : ""} ${
							img.isDamage ? "has-damage" : ""
						}`}
						on:click={() => (idx = index)}
						on:keydown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								idx = index;
							}
						}}
						aria-label={`View ${img.imgSide}`}
					>
						<img
							src={img.imgUrl}
							alt={`Thumbnail of ${img.imgSide}`}
						/>
						{#if img.isDamage || img.damageAttribute === "new-damage"}
							<span class="alert-dot" aria-hidden="true" />
						{/if}
					</button>
				{/each}
			</div>
		</div>
	{/if}
</div>

<style>
	.compare-card {
		padding: 12px 18px 8px;
		display: flex;
		flex-direction: column;
		height: 100%;
		width: 100%;
	}

	.pill {
		display: flex;
		padding: 4px 8px;
		justify-content: center;
		align-items: center;
		border-radius: 4px;
		background: #1b1b20;
		color: #fff;
		font-size: 12px;
		font-weight: 700;
		text-transform: uppercase;
		white-space: nowrap;
	}

	.pill-green {
		background: var(--fds-success);
	}

	.details {
		margin-top: 24px;
		display: grid;
		gap: 24px;
	}

	.details-row {
		display: flex;
		align-items: center;
		gap: 4px;
		justify-content: flex-start;
		font-weight: 400;
		font-style: normal;
		font-size: 16px;
	}

	.details-row.space-between {
		justify-content: space-between;
		width: 100%;
	}

	.info-block {
		flex: 1;
	}

	.tag {
		padding: 4px 8px;
		border-radius: 4px;
		font-size: 12px;
		font-weight: 700;
		text-transform: uppercase;
	}

	.tag-pre {
		background: var(--fds-pending);
		color: var(--fds-on-pending);
	}

	.tag-post {
		background: var(--fds-primary);
		color: var(--fds-on-primary);
	}

	.tag-active {
		background: var(--fds-success);
		color: var(--fds-on-primary);
	}

	hr {
		margin: 24px 0;
	}

	.image-section {
		display: flex;
		flex-direction: column;
		gap: 24px;
		align-items: center;
	}

	.image-title {
		font-size: 16px;
	}

	.image-viewer {
		display: grid;
		grid-template-columns: auto 1fr auto;
		align-items: center;
		gap: 8px;
		justify-items: center;
	}

	.image-nav {
		border: none;
		background: transparent;
		padding: 6px;
		cursor: pointer;
		width: 30px;
		height: 30px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.image-nav:disabled {
		opacity: 0.3;
		cursor: not-allowed;
	}

	.arrow-disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	.image-frame {
		position: relative;
	}

	.image-frame.damage {
		border: 2px solid var(--fds-error);
	}

	.image-frame img {
		width: 100%;
	}

	.radio-group {
		display: grid;
		gap: 6px;
		margin-top: 0;
		align-items: flex-start;
		justify-items: flex-start;
		min-height: 50px;
	}

	.radio-option {
		display: flex;
		align-items: center;
		gap: 8px;
		font-size: 16px;
	}

	.radio-option input {
		width: 20px;
		height: 20px;
		accent-color: var(--fds-primary);
		margin-top: 2px;
	}

	.thumbnails {
		display: flex;
		gap: 8px;
		justify-content: center;
	}

	.thumbnail {
		position: relative;
		border: 2px solid transparent;
		border-radius: 0;
		padding: 0;
		background: transparent;
		cursor: pointer;
		width: 40px;
		height: 40px;
		overflow: visible;
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.thumbnail img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		border-radius: 0;
		display: block;
	}

	.thumbnail.has-damage {
		border-color: var(--fds-error);
	}

	.alert-dot {
		position: absolute;
		top: -11px;
		right: -7px;
		width: 24px;
		height: 24px;
		background-color: var(--fds-surface);
		background-image: url("/assets/icon-alert-filled-red.svg");
		background-size: cover;
		background-repeat: no-repeat;
		border-radius: 50%;
		z-index: 2;
	}

	.link-text {
		color: var(--fds-primary);
		text-decoration-line: underline;
		text-decoration-style: solid;
	}

	.btn-link-reset {
		background: none;
		border: none;
		padding: 0;
		font: inherit;
		cursor: pointer;
		text-align: inherit;
	}
</style>
